import type { ReactNode } from "react";

import AIAstrologerCTA from "@/components/AIAstrologerCTA";
import { ReferenceTable } from "@/components/ReferenceTable";
import Term from "@/components/Term";
import { REF_NAKSHATRAS_EN, type ReferenceDataset } from "@/lib/reference-tables.generated";
import { type FAQItem } from "@/lib/schema";
import { MahadashaMeanings } from "./_mahadasha-meanings";
import { AntardashaDepth } from "./_antardasha-depth";
import DashaExplorer from "./DashaExplorer";

// The 27 nakshatras and their Vimshottari lords: the generated nakshatra table
// (nakshatras.yaml), cut to the columns this section is about.
const NAKSHATRA_LORD_COLUMNS = ["#", "Nakshatra", "Lord"] as const;
const NAKSHATRA_LORDS: ReferenceDataset = {
  id: REF_NAKSHATRAS_EN.id,
  caption: "The 27 nakshatras and the planet that rules each (its Vimshottari Mahadasha lord)",
  columns: NAKSHATRA_LORD_COLUMNS,
  rows: REF_NAKSHATRAS_EN.rows.map((cells) =>
    NAKSHATRA_LORD_COLUMNS.map((column) => cells[REF_NAKSHATRAS_EN.columns.indexOf(column)] ?? "")
  ),
};

const DASHA_FAQS: FAQItem[] = [
  {
    question: "What is a Mahadasha in Vedic astrology?",
    answer:
      "A Mahadasha is a major planetary period in the Vimshottari Dasha system. Each of the nine Vedic planets rules a specific number of years: Ketu (7), Venus (20), Sun (6), Moon (10), Mars (7), Rahu (18), Jupiter (16), Saturn (19), and Mercury (17). During a Mahadasha, the ruling planet's significations dominate your life experiences, career direction, relationships, and spiritual growth.",
  },
  {
    question: "How do I know which Mahadasha I am currently in?",
    answer:
      "Enter your birth details above to calculate your Mahadasha sequence. The first Mahadasha is determined by your Moon's nakshatra at birth. From the balance of the first period, subsequent Mahadashas follow the fixed sequence: Ketu, Venus, Sun, Moon, Mars, Rahu, Jupiter, Saturn, Mercury.",
  },
  {
    question: "What is the difference between Mahadasha and Antardasha?",
    answer:
      "Mahadasha is the major period, while Antardasha is a sub-period within each Mahadasha. Each Mahadasha contains nine Antardashas. The Mahadasha sets the overall theme, and the Antardasha modifies it.",
  },
  {
    question: "Can Dasha periods predict specific events?",
    answer:
      "Vedic astrologers use the Dasha system to identify the timing of major life events such as career changes, marriage, childbirth, and financial shifts. By analysing which Mahadasha, Antardasha, and Pratyantardasha are active, predictions can be narrowed to specific months or even weeks.",
  },
  {
    question: "What is the Mahadasha sequence order?",
    answer:
      "The Vimshottari Mahadasha sequence is fixed: Ketu (7 years), Venus (20), Sun (6), Moon (10), Mars (7), Rahu (18), Jupiter (16), Saturn (19), and Mercury (17) — totalling 120 years. Every person moves through the same order; what differs is the starting point, which is set by the Moon's nakshatra at birth. So one person may begin life in Venus Mahadasha while another begins in Rahu Mahadasha.",
  },
  {
    question: "What happens during Ketu Mahadasha?",
    answer:
      "Ketu Mahadasha lasts 7 years and is traditionally a period of detachment, spiritual searching, and the surfacing of old karmic patterns. Worldly goals can suddenly feel less compelling, and unexpected changes in career or relationships are classically associated with it. Jyotish texts recommend meditation, seva, and Ketu-related upay during this period; how it actually unfolds depends on Ketu's placement in your kundali.",
  },
  {
    question: "What changes when my Antardasha changes?",
    answer:
      "When the Antardasha (sub-period) lord changes, the focus within your Mahadasha shifts: the new sub-lord's houses and significations activate, results speed up or slow down depending on how friendly the two planets are, and the overall tone of the period changes. A demanding Mahadasha can ease considerably in a supportive Antardasha. Enter your birth details above to see your current Mahadasha and its sequence.",
  },
  {
    question: "Is birth time required for Dasha calculation?",
    answer:
      "Birth time is optional but recommended. The Moon moves through one nakshatra in approximately one day, so even without exact birth time the nakshatra can usually be determined. However, exact birth time is needed to calculate the precise Dasha balance.",
  },
  {
    question: "How long does each antardasha last?",
    answer:
      "An antardasha's length is not fixed in years — it is a proportion of its parent Mahadasha. The rule is (Mahadasha years × the sub-lord's own dasha years) ÷ 120. So within a 20-year Venus Mahadasha, the Venus antardasha runs about 3 years 4 months while the Sun antardasha runs about 1 year; within a 6-year Sun Mahadasha every sub-period is proportionally shorter. The sub-lord with the longest cycle (Venus, 20 years) always owns the biggest slice of any Mahadasha, and the shortest (Sun, 6 years) the smallest.",
  },
  {
    question: "How many antardashas are there in one mahadasha, and which comes first?",
    answer:
      "Every Mahadasha contains exactly nine antardashas — one for each of the nine planets. The first antardasha always belongs to the Mahadasha lord itself (so a Venus Mahadasha opens with a Venus–Venus period), after which the sub-periods continue the Vimshottari cycle from the Mahadasha lord (so a Jupiter Mahadasha runs Jupiter, Saturn, Mercury, Ketu, Venus, Sun, Moon, Mars, Rahu). The nine sub-periods always sum back to the full Mahadasha length.",
  },
  {
    question: "Why does the same antardasha feel different for two people?",
    answer:
      "Because a dasha does not deliver a planet's generic reputation — it delivers whatever that planet promised in your specific birth chart. The same Venus antardasha uplifts one person and strains another purely from Venus's natal placement: which houses it owns from the lagna, which house it sits in, its sign dignity, and whether it is afflicted, retrograde or combust. The relationship between the Mahadasha lord and the antardasha lord (friend, neutral or enemy) and the current transits to both then decide how and when the results actually show up.",
  },
];

export function DashaSeoContent({
  crossLinks,
  locale = "en",
}: {
  crossLinks: ReactNode;
  locale?: "en" | "hi";
}) {
  // The Hindi route renders its own Hindi guide, explorer, FAQ and chat entry
  // (HindiSEOSection) below the client, so on hi this keeps only the
  // cross-links (no English guide and no English FAQPage schema).
  if (locale === "hi") {
    return <section className="mx-auto max-w-2xl px-4 sm:px-6 pb-12">{crossLinks}</section>;
  }
  return (
    <section
      className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16"
      style={{ color: "var(--ish-text-muted)" }}
    >
      <h2
        className="text-xl font-bold mb-4"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        What is Vimshottari Dasha?
      </h2>
      <p className="text-body leading-relaxed mb-4">
        Vimshottari <Term k="dasha">Dasha</Term> is the most widely used planetary period system in
        Vedic astrology (Jyotish). The word &ldquo;Vimshottari&rdquo; means &ldquo;120&rdquo; in
        Sanskrit, referring to the total cycle of 120 years across nine planetary periods. Each
        period, called a <Term k="mahadasha">Mahadasha</Term>, is governed by one of the nine Vedic
        planets (Navagraha) and colours all events, decisions, and experiences during that time.
      </p>
      <p className="text-body leading-relaxed mb-6">
        Your Mahadasha sequence is determined by the <Term k="nakshatra">nakshatra</Term> (lunar
        mansion) occupied by the Moon at the exact moment of your birth. Since there are 27
        nakshatras and each is ruled by one of the nine planets, the starting Mahadasha lord &mdash;
        and the remaining balance of that first period &mdash; is unique to each birth chart. This
        is why two people born on the same day but at different times can experience vastly
        different life trajectories.
      </p>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Mahadasha and Antardasha Explained
      </h2>
      <p className="text-body leading-relaxed mb-4">
        <strong style={{ color: "var(--ish-dark)" }}>Mahadasha (Major Period):</strong> The primary
        planetary period that sets the overarching theme. Each Mahadasha lasts a fixed number of
        years: Ketu (7), Venus (20), Sun (6), Moon (10), Mars (7), Rahu (18), Jupiter (16), Saturn
        (19), Mercury (17). The total equals 120 years.
      </p>
      <p className="text-body leading-relaxed mb-4">
        <strong style={{ color: "var(--ish-dark)" }}>
          <Term k="antardasha">Antardasha</Term> (Sub-Period):
        </strong>{" "}
        Each Mahadasha is subdivided into nine Antardashas (Bhukti periods), one for each planet in
        the same Vimshottari sequence. The Antardasha modifies the Mahadasha theme &mdash; for
        example, Jupiter Antardasha within Saturn Mahadasha combines discipline with wisdom and
        expansion.
      </p>
      <p className="text-body leading-relaxed mb-6">
        <strong style={{ color: "var(--ish-dark)" }}>Pratyantardasha (Sub-Sub-Period):</strong> For
        even finer timing, each Antardasha is further divided into Pratyantardashas using the same
        proportional system. This fractal structure allows Vedic astrology to predict the timing of
        specific events with remarkable precision, often to within weeks or months.
      </p>

      <div className="my-8">
        <DashaExplorer />
      </div>

      <MahadashaMeanings />

      <AntardashaDepth />

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        How the Moon Nakshatra Determines Your Dasha
      </h2>
      <p className="text-body leading-relaxed mb-4">
        The 27 nakshatras are distributed among the nine planets in groups of three. Ashwini, Magha,
        and Moola are ruled by Ketu. Bharani, Purva Phalguni, and Purva Ashadha are ruled by Venus.
        Krittika, Uttara Phalguni, and Uttara Ashadha are ruled by the Sun.
      </p>
      <ReferenceTable dataset={NAKSHATRA_LORDS} minWidthClass="min-w-[320px]" />
      <p className="text-body leading-relaxed mb-6">
        The ruling planet of your Moon&apos;s nakshatra becomes your starting Mahadasha lord. The
        degree of the Moon within the nakshatra determines the Dasha balance — how much of the first
        period remains at birth. Our calculator determines your Moon nakshatra from precise Swiss
        Ephemeris calculations and maps it to the correct starting Mahadasha lord.
      </p>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {DASHA_FAQS.map((item) => (
          <details key={item.question} className="group">
            <summary
              className="text-body font-semibold cursor-pointer"
              style={{ color: "var(--ish-dark)" }}
            >
              {item.question}
            </summary>
            <p className="mt-2 text-body-small leading-relaxed pl-4">{item.answer}</p>
          </details>
        ))}
      </div>

      <AIAstrologerCTA context="kundli" />
      {crossLinks}
    </section>
  );
}
